import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { ComposeRule } from '#src/components/leaderboard/ComposeRules/ComposeRule/index.tsx';
import type { FormatRule } from '#src/components/so5/Rules/types.ts';

import type { TaskRules_so5RuleInterface } from './__generated__/index.graphql.ts';

type Props = {
  displayedTypedRules: TaskRules_so5RuleInterface[];
};

export const TaskRules = ({ displayedTypedRules }: Props) => {
  const normalizedRules: FormatRule[] = displayedTypedRules.flatMap(
    ({ __typename, descriptions }) => {
      return (
        descriptions?.map((description, i) => ({
          id: `${__typename}-${i}`,
          icon: description.icon && (
            <img
              className="object-contain"
              src={description.icon}
              alt=""
              width={16}
              height={16}
            />
          ),
          label: description.title,
          description: (
            <Vertical as="span" gap={0}>
              {description.items.map((item, j) => (
                // oxlint-disable-next-line react/no-array-index-key
                <Horizontal key={j} as="span">
                  {!!item.icon && (
                    <img
                      className="object-contain"
                      src={item.icon}
                      alt=""
                      width={16}
                      height={16}
                    />
                  )}
                  {item.title}
                </Horizontal>
              ))}
            </Vertical>
          ),
        })) || []
      );
    }
  );

  if (normalizedRules.length === 0) {
    return null;
  }

  return (
    <Vertical gap={1.5}>
      <LabelL bold>
        <FormattedMessage {...fantasy.rules} />
      </LabelL>
      {normalizedRules.map(rule => (
        <ComposeRule key={rule.id} rule={rule} />
      ))}
    </Vertical>
  );
};

TaskRules.fragments = {
  so5RuleInterface: gql`
    fragment TaskRules_so5RuleInterface on So5RuleInterface {
      id
      descriptions {
        icon
        title
        items {
          icon
          title
        }
      }
    }
  ` as TypedDocumentNode<TaskRules_so5RuleInterface>,
};
